import React, { useState } from "react";
import { api, todayISO } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Download, Eye, FileText, FileSpreadsheet } from "lucide-react";

export default function AdminReports() {
  const [weekStart, setWeekStart] = useState(todayISO());
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const fetchBlob = async (path, params) => {
    const resp = await api.get(path, { params, responseType: "blob" });
    return resp.data;
  };

  const dl = async (path, params, filename) => {
    const blob = await fetchBlob(path, params);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const view = async (path, params) => {
    const blob = await fetchBlob(path, params);
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank", "noopener,noreferrer");
    // do not revoke immediately; keep usable for new tab
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  return (
    <div className="space-y-5" data-testid="admin-reports-page">
      <header>
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Admin</p>
        <h1 className="text-3xl font-extrabold font-display tracking-tighter text-slate-900 mt-1">Reports &amp; Exports</h1>
        <p className="text-sm text-slate-500 mt-1">Generate, view, and download branded payroll and submission reports.</p>
      </header>

      <Card className="rounded-2xl border-slate-200">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-start gap-3">
            <FileText className="h-5 w-5 text-sky-700 mt-1" />
            <div>
              <h3 className="font-bold font-display">Weekly Payroll Report</h3>
              <p className="text-sm text-slate-600">Branded PDF and CSV of every paystub in the selected week.</p>
            </div>
          </div>
          <div className="flex flex-wrap items-end gap-2 pt-2">
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">Week (any day)</Label>
              <Input type="date" value={weekStart} onChange={(e) => setWeekStart(e.target.value)} className="w-48" data-testid="reports-week-input" />
            </div>
            <Button onClick={() => view("/reports/payroll.pdf", { week_start: weekStart })} className="bg-sky-600 hover:bg-sky-700" data-testid="view-payroll-pdf-btn">
              <Eye className="h-4 w-4 mr-1" /> View PDF
            </Button>
            <Button variant="outline" onClick={() => dl("/reports/payroll.pdf", { week_start: weekStart }, `payroll_${weekStart}.pdf`)} data-testid="dl-payroll-pdf-btn">
              <Download className="h-4 w-4 mr-1" /> PDF
            </Button>
            <Button variant="outline" onClick={() => dl("/reports/payroll.csv", { week_start: weekStart }, `payroll_${weekStart}.csv`)} data-testid="dl-payroll-csv-btn">
              <FileSpreadsheet className="h-4 w-4 mr-1" /> CSV
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-2xl border-slate-200">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-start gap-3">
            <FileText className="h-5 w-5 text-sky-700 mt-1" />
            <div>
              <h3 className="font-bold font-display">Daily Submissions Report</h3>
              <p className="text-sm text-slate-600">All daily submissions in a date range — hours, production, status.</p>
            </div>
          </div>
          <div className="flex flex-wrap items-end gap-2 pt-2">
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">From</Label>
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-48" data-testid="reports-from-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">To</Label>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-48" data-testid="reports-to-input" />
            </div>
            <Button onClick={() => view("/reports/submissions.pdf", { date_from: from || undefined, date_to: to || undefined })} className="bg-sky-600 hover:bg-sky-700" data-testid="view-submissions-pdf-btn">
              <Eye className="h-4 w-4 mr-1" /> View PDF
            </Button>
            <Button variant="outline" onClick={() => dl("/reports/submissions.pdf", { date_from: from || undefined, date_to: to || undefined }, "submissions.pdf")} data-testid="dl-submissions-pdf-btn">
              <Download className="h-4 w-4 mr-1" /> PDF
            </Button>
            <Button variant="outline" onClick={() => dl("/reports/submissions.csv", { date_from: from || undefined, date_to: to || undefined }, "submissions.csv")} data-testid="dl-submissions-csv-btn">
              <FileSpreadsheet className="h-4 w-4 mr-1" /> CSV
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
